import React from "react";
import ReactDOM from "react-dom";
import * as Sentry from "@sentry/browser";
import { BrowserRouter, Route } from "react-router-dom";
import URLSearchParams from "@ungap/url-search-params";
import store from "store2";

import { Provider, TypedUseSelectorHook, useSelector } from "react-redux";
import { createDevTools } from "redux-devtools";
import { compose, createStore, applyMiddleware, AnyAction } from "redux";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import { responsiveStoreEnhancer } from "redux-responsive";
import LogMonitor from "redux-devtools-log-monitor";
import DockMonitor from "redux-devtools-dock-monitor";
import thunk, { ThunkDispatch } from "redux-thunk";
import { createBrowserHistory } from "history";

import * as serviceWorker from "./serviceWorkerRegistration";
import apiRequestMiddleware from "./redux/middleware/apiRequestMiddleware";
import consoleLogMiddleware from "./redux/middleware/consoleLogMiddleware";
import reducers from "./reducers";
import App from "./App";
import "./index.css";
import "./common/lib/gamepad";
import "./common/lib/keyboard";
import config from "./common/config";

// Polyfill URLSearchParams so fetch-client works in IE
window.URLSearchParams = URLSearchParams;

Sentry.init({
  dsn: "https://623d0fe10ef84d599b16737d6f102e2f@o1149745.ingest.sentry.io/6262263",
  environment: config.sentryEnvironment,
});

const params = new URLSearchParams(window.location.search);
if (params.get("token")) {
  console.log("Setting API token from URL param");
  store.set("token", params.get("token"));
}

if (params.get("model")) {
  console.log("Setting model from URL param");
  store.set("model", params.get("model"));
}

if (!store.get("token")) {
  throw new Error("No API token defined");
} else {
  console.log("Using API token from local storage");
}

if (!store.get("model")) {
  throw new Error("No hardware model defined");
} else {
  console.log("Using hardware model from local storage");
}

// Apply Redux middleware
let storeEnhancer = compose(
  responsiveStoreEnhancer,
  applyMiddleware(apiRequestMiddleware, thunk, consoleLogMiddleware)
);

// Apply Redux DevTools in development only
let DevTools = null;

if (process.env.REACT_APP_ENV === "development") {
  DevTools = createDevTools(
    <DockMonitor
      toggleVisibilityKey="ctrl-h"
      changePositionKey="ctrl-q"
      defaultIsVisible={false}
    >
      <LogMonitor theme="tomorrow" />
    </DockMonitor>
  );

  storeEnhancer = compose(storeEnhancer, DevTools.instrument());
}

// Bootstrap the React app
const reduxStore = storeEnhancer(createStore)(reducers);
const persistor = persistStore(reduxStore);

const history = createBrowserHistory();

ReactDOM.render(
  <Provider store={reduxStore}>
    <PersistGate loading={null} persistor={persistor}>
      <BrowserRouter history={history}>
        <div>
          <Route component={App} />
          <Route component={DevTools} />
        </div>
      </BrowserRouter>
    </PersistGate>
  </Provider>,
  document.getElementById("root")
);

serviceWorker.register();

export type RootState = ReturnType<typeof reduxStore.getState>;
export type AppDispatch = typeof reduxStore.dispatch;
export type TypedDispatch = ThunkDispatch<RootState, any, AnyAction>;
export type History = typeof history;
export type Location = typeof history.location;
export const useRootSelector: TypedUseSelectorHook<RootState> = useSelector;
